"use client";

import Image, { ImageProps } from "next/image";
import {
  type LucideIcon,
  University,
  Newspaper,
  GraduationCap,
  Briefcase,
  ListCheck,
} from "lucide-react";
import { useState, useEffect } from "react";

type CustomImageProps = Omit<ImageProps, "onError"> & {
  className?: string;
  iconClassName?: string;
  withBackground?: boolean;
};

type GenericFallbackImageProps = CustomImageProps & {
  Icon: LucideIcon;
  strokeWidth?: number;
};

const FallbackContainer = ({
  children,
  className,
  withBackground = true,
  fill,
}: {
  children: React.ReactNode;
  className?: string;
  withBackground?: boolean;
  fill?: boolean;
}) => (
  <div
    className={`flex flex-col items-center justify-center text-primary w-full h-full ${
      withBackground ? "bg-gray-300" : ""
    } ${fill ? "absolute inset-0" : ""} ${className || ""}`}
  >
    {children}
  </div>
);

const GenericFallbackImage = ({
  Icon,
  iconClassName,
  className,
  strokeWidth = 1.5,
  fill,
  withBackground,
  ...props
}: GenericFallbackImageProps) => {
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setIsError(false);
  }, [props.src]);

  if (isError || !props.src) {
    return (
      <FallbackContainer
        className={className}
        withBackground={withBackground}
        fill={fill}
      >
        <Icon
          className={iconClassName || "size-20"}
          strokeWidth={strokeWidth}
        />
      </FallbackContainer>
    );
  }

  return (
    <Image
      className={className}
      fill={fill}
      {...props}
      onError={() => setIsError(true)}
    />
  );
};

export const UniversityImage = (props: CustomImageProps) => (
  <GenericFallbackImage Icon={University} {...props} />
);

export const ArticleImage = (props: CustomImageProps) => (
  <GenericFallbackImage Icon={Newspaper} {...props} />
);

export const SpecializationImage = (props: CustomImageProps) => (
  <GenericFallbackImage Icon={GraduationCap} {...props} />
);

export const ServiceImage = (props: CustomImageProps) => (
  <GenericFallbackImage Icon={Briefcase} {...props} />
);

export const ServiceIconImage = (props: CustomImageProps) => (
  <GenericFallbackImage strokeWidth={2.5} Icon={ListCheck} {...props} />
);
